import { get, post, remove } from "../helpers/apiHelper";
import { Api } from "../constants";
import { API_RESPONSE_STATUS } from "../utils/constants";
import { ApiResponseStatus } from "../types";
import {
  BroadcastNotificationRequest,
  BroadcastNotificationResponse,
  DeleteNotificationResponse,
  DeviceRegistrationRequest,
  DeviceRegistrationResponse,
  DeviceRemovalResponse,
  GetUserNotificationsResponse,
  ReadNotificationResponse,
  SendNotificationRequest,
  SendNotificationResponse,
  UserNotification,
} from "../types";

export function extractNotificationsFromApiPayload(
  raw: unknown
): UserNotification[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw == null || typeof raw !== "object") {
    return [];
  }
  const obj = raw as Record<string, unknown>;
  const candidates = [
    obj.data,
    obj.notifications,
    obj.items,
    obj.content,
    obj.results,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as UserNotification[];
    }
  }
  const inner = obj.data;
  if (inner && typeof inner === "object") {
    const innerObj = inner as Record<string, unknown>;
    for (const key of ["notifications", "items", "content", "results"] as const) {
      const arr = innerObj[key];
      if (Array.isArray(arr)) {
        return arr as UserNotification[];
      }
    }
  }
  return [];
}

function readApiString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function readPayloadMessage(payload: unknown): string {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const record = payload as Record<string, unknown>;
  return (
    readApiString(record.message) ||
    readApiString(record.body) ||
    readApiString(record.text) ||
    readApiString(record.description)
  );
}

function isNotificationRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return Boolean(
    readApiString(record.notificationId) ||
    readApiString(record.id) ||
    readApiString(record._id)
  );
}

/**
 * Normalizes GET /notification/{id} payloads.
 * Supports the standard envelope (`{ status, message, data: { notificationId, title, message, ... } }`)
 * and the unwrapped `data` object returned by apiHelper.
 */
export function parseNotificationFromApiResponse(
  raw: unknown
): UserNotification | null {
  if (raw == null || typeof raw !== "object") {
    return null;
  }

  const obj = raw as Record<string, unknown>;

  if (isNotificationRecord(obj.data)) {
    return parseNotificationFromApiResponse(obj.data);
  }

  if (!isNotificationRecord(obj)) {
    return null;
  }

  const notificationId =
    readApiString(obj.notificationId) ||
    readApiString(obj.id) ||
    readApiString(obj._id);

  const title =
    readApiString(obj.title) ||
    readApiString(obj.subject) ||
    readApiString(obj.heading);

  const message =
    readApiString(obj.message) ||
    readApiString(obj.body) ||
    readApiString(obj.content) ||
    readApiString(obj.description) ||
    readPayloadMessage(obj.payload);

  if (!title && !message) {
    return null;
  }

  const levelRaw =
    readApiString(obj.level) || readApiString(obj.severity) || "INFO";
  const normalizedLevel = levelRaw.toUpperCase();
  const level = (
    ["INFO", "SUCCESS", "WARNING", "ERROR"].includes(normalizedLevel)
      ? normalizedLevel
      : "INFO"
  ) as UserNotification["level"];

  const chatIdValue =
    obj.chatId ?? obj.roomId ?? obj.chat_id ?? obj.room_id ?? null;
  const chatId =
    chatIdValue == null || chatIdValue === ""
      ? null
      : String(chatIdValue);

  return {
    notificationId: notificationId || undefined,
    id: readApiString(obj.id) || undefined,
    _id: readApiString(obj._id) || undefined,
    type: readApiString(obj.type) || undefined,
    level,
    title: title || "Notification",
    message,
    createdAt:
      readApiString(obj.createdAt) ||
      readApiString(obj.created_at) ||
      new Date().toISOString(),
    chatId,
    read: typeof obj.read === "boolean" ? obj.read : undefined,
    isRead: typeof obj.isRead === "boolean" ? obj.isRead : undefined,
  };
}

/**
 * Register a device for push notifications (POST `{BASE_URL}/notification/register/device`).
 * Web clients send `platform: "WEB"` — see `registerDeviceForNotifications` in `notificationSlice`.
 * Uses the default axios auth header (`user-access-token`); ensure the user is logged in.
 */
export const registerDevice = async (
  data: DeviceRegistrationRequest
): Promise<DeviceRegistrationResponse> => {
  const url = `${Api.notification}/register/device`;
  return await post<DeviceRegistrationResponse>(url, data);
};

/**
 * Remove a registered device token from notifications
 * @param fcmToken - Firebase device token to remove
 * @returns Promise resolving to DeviceRemovalResponse
 */
export const removeDevice = async (
  fcmToken: string
): Promise<DeviceRemovalResponse> => {
  const url = `${Api.notification}/remove/device?fcmToken=${encodeURIComponent(
    fcmToken
  )}`;
  return await remove<DeviceRemovalResponse>(url);
};

/**
 * Send a notification to a user
 * @param data - Notification content and routing details
 * @returns Promise resolving to SendNotificationResponse
 */
export const sendNotification = async (
  data: SendNotificationRequest
): Promise<SendNotificationResponse> => {
  const url = `${Api.notification}/send`;
  return await post<SendNotificationResponse>(url, data);
};

/**
 * Broadcast a notification to all users
 * @param data - Notification content and level
 * @returns Promise resolving to BroadcastNotificationResponse
 */
export const broadcastNotification = async (
  data: BroadcastNotificationRequest
): Promise<BroadcastNotificationResponse> => {
  const url = `${Api.notification}/broadcast`;
  return await post<BroadcastNotificationResponse>(url, data);
};

/**
 * Fetch all notifications for a user
 * @param userId - User identifier
 * @returns Promise resolving to GetUserNotificationsResponse
 */
export const getUserNotifications = async (
  userId: string,
  nextPageKey: string | null = null,
  limit: number = 10
): Promise<GetUserNotificationsResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(limit));
  if (nextPageKey) {
    queryParams.set("nextPageKey", nextPageKey);
  }
  const query = queryParams.toString();
  const url = `${Api.notification}/user/${encodeURIComponent(userId)}?${query}`;
  /**
   * apiHelper.get unwraps `response.data.data` when present; backends may still
   * return `{ notifications: [...] }` or nested `data`. Normalize always.
   */
  const result = await get<unknown>(url);
  const list = extractNotificationsFromApiPayload(result);
  const pageData =
    result && typeof result === "object" ? (result as Record<string, unknown>) : null;
  const apiNextPageKey =
    pageData && "nextPageKey" in pageData
      ? (pageData.nextPageKey as string | null)
      : null;
  const total =
    pageData && typeof pageData.total === "number" ? pageData.total : list.length;
  const message =
    result &&
    typeof result === "object" &&
    typeof (result as { message?: unknown }).message === "string"
      ? (result as { message: string }).message
      : "";
  return {
    status: API_RESPONSE_STATUS.SUCCESS as ApiResponseStatus,
    statusCode: 200,
    message,
    timestamp: new Date().toISOString(),
    data: {
      notifications: list,
      nextPageKey: apiNextPageKey,
      total,
    },
  };
};

/**
 * Fetch a notification by id
 * @param notificationId - Notification identifier
 * @returns Promise resolving to the normalized notification
 */
export const getNotificationById = async (
  notificationId: string
): Promise<UserNotification> => {
  const url = `${Api.notification}/${encodeURIComponent(notificationId)}`;
  const result = await get<unknown>(url);
  const notification = parseNotificationFromApiResponse(result);
  if (!notification) {
    throw new Error("Invalid notification response");
  }
  return notification;
};

/**
 * Mark a notification as read
 * @param notificationId - Notification identifier
 * @returns Promise resolving to ReadNotificationResponse
 */
export const readNotification = async (
  notificationId: string,
  userId: string
): Promise<ReadNotificationResponse> => {
  const url = `${Api.notification}/read/${encodeURIComponent(notificationId)}?userId=${encodeURIComponent(userId)}`;
  return await post<ReadNotificationResponse>(url);
};

/**
 * Delete a notification
 * @param notificationId - Notification identifier
 * @returns Promise resolving to DeleteNotificationResponse
 */
export const deleteNotification = async (
  notificationId: string,
  userId: string
): Promise<DeleteNotificationResponse> => {
  const url = `${Api.notification}/${encodeURIComponent(notificationId)}?userId=${encodeURIComponent(userId)}`;
  return await remove<DeleteNotificationResponse>(url);
};
